import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useFeature } from './hooks/useFeature';
import { FEATURE_IDS } from './constants/feature-registry';

// Lazy load pages for better performance
const HomePage = React.lazy(() => import('./pages/HomePage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const OperationsDashboardPage = React.lazy(() => import('./pages/OperationsDashboardPage'));
const FinancialDashboardPage = React.lazy(() => import('./pages/FinancialDashboardPage'));
const CalendarPage = React.lazy(() => import('./pages/CalendarBoardView'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const ProfileSetupPage = React.lazy(() => import('./pages/ProfileSetupPage'));
const ConfirmSignupPage = React.lazy(() => import('./pages/ConfirmSignupPage'));
const StudentLoginPage = React.lazy(() => import('./pages/StudentLoginPage'));
const TutorLoginPage = React.lazy(() => import('./pages/TutorLoginPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const StudentPortalPage = React.lazy(() => import('./pages/StudentPortalPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// Students - using new modal-based form from app directory
const StudentsPage = React.lazy(() => import('./app/students/page'));
const StudentsShowPage = React.lazy(() => import('./pages/students/StudentsShowPage'));

// Lessons
const LessonsListPage = React.lazy(() => import('./pages/lessons/LessonsListPage'));

const LessonsEditPage = React.lazy(() => import('./pages/lessons/LessonsEditPage'));
const LessonsShowPage = React.lazy(() => import('./pages/lessons/LessonsShowPage'));

// Invoices
const InvoicesListPage = React.lazy(() => import('./pages/invoices/InvoicesListPage'));
const InvoicesCreatePage = React.lazy(() => import('./pages/invoices/InvoicesCreatePage'));
const InvoicesEditPage = React.lazy(() => import('./pages/invoices/InvoicesEditPage'));
const InvoicesShowPage = React.lazy(() => import('./pages/invoices/InvoicesShowPage'));

// Locations
const LocationsListPage = React.lazy(() => import('./pages/locations/LocationsListPage'));
const LocationsCreatePage = React.lazy(() => import('./pages/locations/LocationsCreatePage'));
const LocationsEditPage = React.lazy(() => import('./pages/locations/LocationsEditPage'));
const LocationsShowPage = React.lazy(() => import('./pages/locations/LocationsShowPage'));
const SchoolPlannerPage = React.lazy(() => import('./pages/school-planner/SchoolPlannerPage'));

// Expenses & tax reporting
const ExpensesListPage = React.lazy(() => import('./pages/expenses/ExpensesListPage'));
const YearSummaryPage = React.lazy(() => import('./pages/reports/YearSummaryPage'));

// AI agent access (MCP)
const AgentAccessPage = React.lazy(() => import('./pages/agent/AgentAccessPage'));
const AgentConfirmPage = React.lazy(() => import('./pages/agent/AgentConfirmPage'));

// Documentation centre
const DocsPage = React.lazy(() => import('./pages/docs/DocsPage'));

// Support cases (Intecco Ledger)
const SupportPage = React.lazy(() => import('./pages/support/SupportPage'));
const SupportCasePage = React.lazy(() => import('./pages/support/SupportCasePage'));

// Subscription
const SubscriptionPage = React.lazy(() => import('./pages/SubscriptionPage'));

// Gig Customers & Invoices
const GigCustomersListPage = React.lazy(() => import('./pages/gig-customers/GigCustomersListPage'));
const GigCustomerFormPage = React.lazy(() => import('./pages/gig-customers/GigCustomerFormPage'));
const GigInvoicesListPage = React.lazy(() => import('./pages/gig-invoices/GigInvoicesListPage'));
const GigInvoiceFormPage = React.lazy(() => import('./pages/gig-invoices/GigInvoiceFormPage'));

// Email Templates
const EmailTemplatesPage = React.lazy(() => import('./pages/email-templates/EmailTemplatesPage'));

// Booking (Tutor management pages only - public booking is in studentBookingApp)
const BookingSessionsListPage = React.lazy(() => import('./pages/booking/BookingSessionsListPage'));
const BookingSessionCreatePage = React.lazy(() => import('./pages/booking/BookingSessionCreatePage'));
const BookingSessionShowPage = React.lazy(() => import('./pages/booking/BookingSessionShowPage'));
const BookingRegistrationsPage = React.lazy(() => import('./pages/booking/BookingRegistrationsPage'));

const StudentPortalRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { enabled, loading } = useFeature(FEATURE_IDS.STUDENT_PORTAL);
  if (loading) return null;
  if (!enabled) return <Navigate to="/dashboard" replace />;
  return children;
};

export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Root route */}
      <Route path="/" element={<HomePage />} />
      
      {/* Dashboard */}
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/operations" element={<OperationsDashboardPage />} />
      <Route path="/financial-dashboard" element={<FinancialDashboardPage />} />
      
      {/* Calendar */}
      <Route path="/calendar" element={<CalendarPage />} />
      
      {/* Profile / Settings */}
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/settings" element={<ProfilePage />} />
      <Route path="/profile-setup" element={<ProfileSetupPage />} />
      
      {/* Authentication */}
      <Route path="/login" element={<TutorLoginPage />} />
      <Route path="/login/student" element={<StudentLoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/confirm-signup" element={<ConfirmSignupPage />} />
      
      {/* Students */}
      <Route path="/students" element={<StudentsPage />} />
      <Route path="/students/show/:id" element={<StudentsShowPage />} />
      
      {/* Lessons */}
      <Route path="/lessons" element={<LessonsListPage />} />
      <Route path="/lessons/edit/:id" element={<LessonsEditPage />} />
      <Route path="/lessons/show/:id" element={<LessonsShowPage />} />
      
      {/* Invoices */}
      <Route path="/invoices" element={<InvoicesListPage />} />
      <Route path="/invoices/create" element={<InvoicesCreatePage />} />
      <Route path="/invoices/edit/:id" element={<InvoicesEditPage />} />
      <Route path="/invoices/show/:id" element={<InvoicesShowPage />} />

      {/* Expenses & tax reporting */}
      <Route path="/expenses" element={<ExpensesListPage />} />
      <Route path="/year-summary" element={<YearSummaryPage />} />

      {/* AI agent access — the page itself shows the early-access notice when off */}
      <Route path="/agent" element={<AgentAccessPage />} />
      <Route path="/agent/confirm/:id" element={<AgentConfirmPage />} />

      {/* Help & documentation */}
      <Route path="/docs" element={<DocsPage />} />
      <Route path="/docs/:slug" element={<DocsPage />} />

      <Route path="/support" element={<SupportPage />} />
      <Route path="/support/:ref" element={<SupportCasePage />} />
      
      {/* Locations */}
      <Route path="/locations" element={<LocationsListPage />} />
      <Route path="/locations/create" element={<LocationsCreatePage />} />
      <Route path="/locations/edit/:id" element={<LocationsEditPage />} />
      <Route path="/locations/show/:id" element={<LocationsShowPage />} />
      <Route path="/school-planner" element={<SchoolPlannerPage />} />
      
      {/* Gig Customers */}
      <Route path="/gig-customers" element={<GigCustomersListPage />} />
      <Route path="/gig-customers/create" element={<GigCustomerFormPage />} />
      <Route path="/gig-customers/edit/:id" element={<GigCustomerFormPage />} />
      
      {/* Gig Invoices */}
      <Route path="/gig-invoices" element={<GigInvoicesListPage />} />
      <Route path="/gig-invoices/create" element={<GigInvoiceFormPage />} />
      <Route path="/gig-invoices/edit/:id" element={<GigInvoiceFormPage />} />
      
      {/* Student Portal */}
      <Route path="/student-portal" element={<StudentPortalRoute><StudentPortalPage /></StudentPortalRoute>} />
      
      {/* Email Templates */}
      <Route path="/email-templates" element={<EmailTemplatesPage />} />
      
      {/* Subscription */}
      <Route path="/subscription" element={<SubscriptionPage />} />
      
      {/* Booking Sessions (Tutor management) */}
      <Route path="/booking-sessions" element={<StudentPortalRoute><BookingSessionsListPage /></StudentPortalRoute>} />
      <Route path="/booking-sessions/create" element={<StudentPortalRoute><BookingSessionCreatePage /></StudentPortalRoute>} />
      <Route path="/booking-sessions/:id" element={<StudentPortalRoute><BookingSessionShowPage /></StudentPortalRoute>} />
      <Route path="/booking-sessions/:sessionId/registrations" element={<StudentPortalRoute><BookingRegistrationsPage /></StudentPortalRoute>} />
      
      {/* Note: Public booking pages (/book/o/:code, /book/f/:code) are now in studentBookingApp */}
      
      {/* Catch all - 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}; 